import enCompiled from '../../../../i18n/generated/en.json';
import neCompiled from '../../../../i18n/generated/ne.json';
import { BRAND_NAME } from '../config/brand';

export type Catalog = Record<string, unknown>;

function isPlainObject(value: unknown): value is Catalog {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function withBrand(catalog: Catalog): Catalog {
  const app = isPlainObject(catalog.app) ? catalog.app : {};
  const brand = isPlainObject(app.brand) ? app.brand : {};
  return {
    ...catalog,
    app: {
      ...app,
      brand: {
        ...brand,
        name: BRAND_NAME,
      },
    },
  };
}

const englishCatalog = withBrand(enCompiled as Catalog);
const nepaliCatalog = withBrand(neCompiled as Catalog);

export const SUPPORTED_LOCALES = {
  en: {
    languageTag: 'en-US',
    catalog: englishCatalog,
  },
  ne: {
    languageTag: 'ne-NP',
    catalog: nepaliCatalog,
  },
} as const;

export type SupportedLocale = keyof typeof SUPPORTED_LOCALES;
export const CATALOG_NAMESPACES = Object.freeze(Object.keys(englishCatalog));

export function normalizeLocale(locale?: string | null): SupportedLocale | null {
  if (!locale) return null;
  const lower = locale.trim().toLowerCase();
  if (lower === 'en' || lower.startsWith('en-')) return 'en';
  if (lower === 'ne' || lower === 'np' || lower === 'ne-np' || lower.startsWith('ne-')) {
    return 'ne';
  }
  return null;
}

export function getLanguageTag(locale: string | undefined | null): string {
  return SUPPORTED_LOCALES[normalizeLocale(locale) ?? 'en'].languageTag;
}

function catalogValue(catalog: Catalog, key: string): unknown {
  const normalized = key.includes(':') ? key.replace(':', '.') : key;
  let current: unknown = catalog;
  for (const segment of normalized.split('.')) {
    if (!isPlainObject(current) || !(segment in current)) return undefined;
    current = current[segment];
  }
  return current;
}

export function catalogHasKey(catalog: Catalog, key: string): boolean {
  const value = catalogValue(catalog, key);
  return typeof value === 'string' && value.trim().length > 0;
}

export function getCatalogText(catalog: Catalog, key: string): string | undefined {
  const value = catalogValue(catalog, key);
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

export function flattenCatalog(
  catalog: Catalog,
  prefix = '',
  out = new Map<string, string>(),
): Map<string, string> {
  for (const [key, value] of Object.entries(catalog)) {
    const next = prefix ? `${prefix}.${key}` : key;
    if (isPlainObject(value)) flattenCatalog(value, next, out);
    else if (typeof value === 'string') out.set(next, value);
  }
  return out;
}
